import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { getDateFromOffset } from '../../../helpers/dates';
import { WelletContext } from '../../../context/WelletContext';
import { skeletonLoading } from '../../../components/Skeletons/SkeletonAccount';
import { useTranslation } from 'react-i18next';
import ContentApp from '../../../components/ContentApp';
import Balance from '../../../components/Balance/Balance';
import EmptyActivity from '../../../components/Common/EmptyActivity/EmptyActivity';
import CustomerNav from '../../../components/CustomerNav/CustomerNav';
import AccountHeader from '../../../components/AccountHeader/AccountHeader';
import WarningBox from '../../../components/Common/WarningBox/WarningBox';
import ActivityReservationItem from '../../../components/Activities/ActivityReservationItem.js/ActivityReservationItem';
import useRealtimeReservations from '../../../hooks/useRealtimeReservations';
import { CircleSpinner } from 'react-spinners-kit';
import AccountFilterIcon from '../../../components/icons/AccountFilterIcon';
import PaymentLink from '../../../components/icons/PaymentLink';
import CustomButton from '../../../components/ButtomCustom/CustomButton';
import ArrowIcon from '../../../components/icons/ArrowIcon';
import InstagramIcon2 from '../../../components/icons/InstagramIcon2';
import QrIconHome from '../../../components/icons/QrIconHome';
import IconReserva from '../../../components/icons/IconReserva';
import Button from '../../../components/Common/Button/Button';
import AccountPercentage from '../../AccountVerification/AccountPercentage';
import useRealtimeVerification from '../../../hooks/useRealtimeVerification';
const Account = ({ history }) => {
  useRealtimeVerification();
  const { t } = useTranslation();
  const welletContext = useContext(WelletContext);
  const jwt = useSelector((state) => state.associates.jwt);
  const user = useSelector((state) => state.associates.user);
  const userWithdrawalMethod = user?.userWithdrawalMethod;
  const today = getDateFromOffset(user.companyUtcMinutesOffset);
  const todayFilter = format(today, 'yyyy-MM-dd');
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [filterDate, setFilterDate] = useState(
    localStorage.getItem('filterDate') ?? 'RESERVATION_DATE',
  );
  const [myEarningsToday, setMyEarningsToday] = useState(null);
  const [monthEarning, setMonthEarning] = useState(0);
  const kycStatus = useSelector((state) => state.associates.user.kycStatus);
  const [selectedOption, setSelectedOption] = useState(filterDate);
  const advantageNetworkStatus = useSelector(
    (state) => state?.associates?.user?.advantageNetworkStatus,
  );

  const [openModal, setOpenModal] = useState(false);
  const app = useSelector((state) => state.app);
  const isLinkMaker = user?.roles?.includes('link');

  const listVerificationUser = user?.verificationUser || [];

  const completedSteps = listVerificationUser.filter((item) => item.status === 'COMPLETED').length;
  const totalSteps = listVerificationUser.length;
  const percentage = Math.round((completedSteps / totalSteps) * 100);

  const OperateConciergeV2 = process.env.REACT_APP_OPERATE_CONCIERGE_V2;

  const isOldUser = useSelector((state) => state.associates.user.isOldUser);

  const methodWithdraw = (user?.verificationUser).find(
    (i) => i.type === 'WITHDRAWAL_METHOD',
  )?.status;

  const wireMethod =
    user.withdrawalMethod === 'WIRE_INTERNATIONAL' || user.withdrawalMethod === 'WIRE_MEXICO';
  const cashOrCertificate =
    user.withdrawalMethod === 'CASH' || user.withdrawalMethod === 'CERTIFICATE';
  const getMyEarningsToday = async () => {
    setLoadingBalance(true);
    try {
      const balance = await welletContext.apis.tickets.get(
        `/associates/GetEarnings?from=${todayFilter}&to=${todayFilter}`,
      );
      setMyEarningsToday(balance.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingBalance(false);
    }
  };

  const getEarnings = async (fromDate, toDate) => {
    setLoadingBalance(true);
    try {
      const balance = await welletContext.apis.tickets.get(
        `/associates/GetEarnings?from=${fromDate}&to=${toDate}`,
      );
      setMonthEarning(balance.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingBalance(false);
    }
  };

  const handleValidReservationUpdate = useCallback(() => {
    getMyEarningsToday();

    const date = getDateFromOffset(user.companyUtcMinutesOffset);
    const firstMonthDate = format(startOfMonth(date), 'yyyy-MM-dd');
    const lastMonthDate = format(endOfMonth(date), 'yyyy-MM-dd');

    getEarnings(firstMonthDate, lastMonthDate);
  }, [getMyEarningsToday, getEarnings]);
  const reservationTypes = {
    ReservationTypeInclude: [],
    ReservationTypeExclude: ['LINK'],
  };

  const { reservations, loading, hasMore, loadingMore, fetchMoreReservations } =
    useRealtimeReservations(today, filterDate, handleValidReservationUpdate, reservationTypes);

  useEffect(() => {
    getMyEarningsToday();
    const date = getDateFromOffset(user.companyUtcMinutesOffset);
    const firstMonthDate = format(startOfMonth(date), 'yyyy-MM-dd');
    const lastMonthDate = format(endOfMonth(date), 'yyyy-MM-dd');

    getEarnings(firstMonthDate, lastMonthDate);
  }, [jwt]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const storedFilterDate = localStorage.getItem('filterDate');
    if (storedFilterDate) {
      setFilterDate(storedFilterDate);
    }
  }, []);

  const isEmptyActivity = reservations?.length === 0 || undefined;

  useEffect(() => {
    if (!localStorage.getItem('filterDate')) localStorage.setItem('filterDate', 'RESERVATION_DATE');
  }, []);

  const handleSelectChange = (selectedOption) => {
    localStorage.setItem('filterDate', selectedOption);
    setFilterDate(selectedOption);
    setSelectedOption(selectedOption);
  };

  const renderWithdrawalMethodWarningBox = () => {
    if (userWithdrawalMethod || cashOrCertificate) return null;

    if (!(methodWithdraw === 'PENDING' && isOldUser)) return null;

    let title = '';
    let subtitle = '';
    let nextUrl = '';

    if (user.withdrawalMethod === 'STRIPE') {
      title = t('noAccountAssociated.STRIPE.title');
      subtitle = t('noAccountAssociated.STRIPE.subtitle');
      nextUrl = '/stripeaccount';
    } else if (user.withdrawalMethod === 'MERCADOPAGO') {
      title = t('noAccountAssociated.mercadoPago.title');
      subtitle = t('noAccountAssociated.mercadoPago.subtitle');
      nextUrl = '/mercadopagoaccount';
    } else if (wireMethod) {
      title = t('noAccountAssociated.WIRE.title');
      subtitle = t('noAccountAssociated.WIRE.subtitle');
      if (user.withdrawalMethod === 'WIRE_INTERNATIONAL') {
        nextUrl = '/international-bank';
      } else if (user.withdrawalMethod === 'WIRE_MEXICO') {
        nextUrl = '/national-bank';
      }
    }

    return (
      <WarningBox
        title={title}
        subtitle={
          <div>
            {subtitle}
            <span style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>
              {t('here')}
            </span>
            .
          </div>
        }
        onClick={() => history.push(nextUrl)}
      />
    );
  };

  return (
    <div>
      <AccountHeader title={app.name} icon={'config'} pathName='/configuration' />

      <ContentApp menu header>
        {/* <div className='mb-2'>
          {advantageNetworkStatus !== 'APPROVED' ? (
            <WarningBox
              title={t('advantageNetworkStatus.title')}
              subtitle={t('advantageNetworkStatus.text2')}
            />
          ) : OperateConciergeV2 && (kycStatus === 'PENDING' || kycStatus === 'REVALIDATION') ? (
            <WarningBox
              title={t('identityVerificationPending')}
              subtitle={
                <div>
                  {t('identityNotBeenVerified')}
                  <span style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>
                    {t('here')}
                  </span>
                  .
                </div>
              }
              onClick={() => history.push('/kyc-start')}
            />
          ) : (
            (kycStatus === 'PENDING' || kycStatus === 'REJECTED') && (
              <WarningBox
                title={t('identityNotValidated.title')}
                subtitle={t('identityNotValidated.subtitle')}
                textLink={t('identityNotValidated.textLink')}
                onClickText={() => history.push('/kyc-start')}
              />
            )
          )}
        </div> */}

        {/* {kycStatusCard === 'PENDING' && isOldUser ? (
          <div className='mb-2'>
            <WarningBox
              title={t('biometricVerificationPending')}
              subtitle={
                <div>
                  {t('verifyBiometricData')}
                  <span style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>
                    {t('here')}
                  </span>
                  .
                </div>
              }
              onClick={() => history.push('/kyc-start')}
            />
          </div>
        ) : (
          renderWithdrawalMethodWarningBox()
        )} */}

        {percentage !== 100 && (
          <AccountPercentage
            percentage={percentage}
            onClick={() => history.push('/account-verification')}
          />
        )}

        <div className='mt-2'>
          <Balance
            title={t('earnings')}
            todayBalance={myEarningsToday?.amount}
            last7DaysBalance={monthEarning?.amount}
            showText={true}
            fontSize='1.8rem'
            showAllBalances={true}
            loading={loadingBalance}
            mpVerified={true}
            showArrow={true}
          />
        </div>

        <div className='d-flex mt-2'>
          {app.hasMarketingCampaign ? (
            <>
              <div className='d-flex flex-column' style={{ width: '50%' }}>
                <CustomButton
                  size='link'
                  text={t('campaigns')}
                  leftIcon={<InstagramIcon2 size={20} fillColor='#000' />}
                  rightIcon={<ArrowIcon size={15} />}
                  onClick={() => history.push('/campaigns-feed')}
                  className='mb-2'
                />

                <CustomButton
                  rightIcon={<ArrowIcon size={15} />}
                  leftIcon={<QrIconHome size={20} fillColor='#000' />}
                  text={t('QR')}
                  onClick={() => history.push('/qr-restaurant')}
                  size='link'
                />
              </div>

              <div className='w-50 ml-2'>
                <CustomButton
                  size={!isLinkMaker ? 'reserva' : 'link'}
                  className='btn-primary btn-account font-normal'
                  fontSize={isLinkMaker ? '1.1rem' : '1.3rem'}
                  rightIcon={<ArrowIcon size={15} />}
                  leftIcon={<IconReserva size={25} fillColor='#000' />}
                  text={t('makeNewReservation')}
                  onClick={() => history.push('/restaurant-list')}
                />
                {isLinkMaker && (
                  <CustomButton
                    size='link'
                    rightIcon={<ArrowIcon size={15} />}
                    leftIcon={<PaymentLink size={25} fillColor='#000' />}
                    text={t('paymentLink')}
                    onClick={() => history.push('/link-home')}
                    className='mt-2'
                  />
                )}
              </div>
            </>
          ) : (
            <Button
              size='large'
              className='col btn-primary btn-account'
              text={t('bookEarnProfits')}
              icon={true}
              typeIcon='arrow'
              iconColor='#000'
              onClick={() => history.push('/restaurant-list')}
            />
          )}
        </div>

        <div className='bold font-normal text-lighter-gray mt-3'>{t('myReservations')}</div>
        <div className='row mt-3 mb-4'>
          <div className='col-auto pr-0'>
            <div className='account-button' onClick={() => history.push('/all-reservations')}>
              <AccountFilterIcon color='#7E7E7E' size='16px' styles={{ marginTop: '-2px' }} />
            </div>
          </div>
          <div className='col-auto px-1'>
            <div
              className={`account-button ${
                selectedOption === 'RESERVATION_DATE' ? 'selected' : ''
              }`}
              onClick={() => handleSelectChange('RESERVATION_DATE')}
            >
              {t('forToday')}
            </div>
          </div>
          <div className='col-auto col-md p-0'>
            <div
              className={`account-button ${selectedOption === 'CREATED_DATE' ? 'selected' : ''}`}
              onClick={() => handleSelectChange('CREATED_DATE')}
            >
              {t('mostRecent')}
            </div>
          </div>
          <div className='col pl-1'>
            <div
              className={`account-button ${
                selectedOption === 'RECENT_COMMISSIONS' ? 'selected' : ''
              }`}
              onClick={() => handleSelectChange('RECENT_COMMISSIONS')}
            >
              {t('lastCommissions')}
            </div>
          </div>
        </div>

        {loading ? (
          skeletonLoading()
        ) : (
          <>
            {isEmptyActivity ? (
              <EmptyActivity subtitle={<div className='bold'>{t('noBookings')}</div>} />
            ) : (
              <>
                {reservations?.map((r, index) => (
                  <div key={index}>
                    <ActivityReservationItem
                      activity={r}
                      date={r?.reservationLocalTime}
                      rightText={r.amount}
                      index={index}
                      lastIndex={reservations.length - 1}
                      handleClick={() => history.push(`/operation-details?id=${r.id}`)}
                    />
                  </div>
                ))}
                {loadingMore ? (
                  <div className='p-3 d-flex justify-content-center'>
                    <CircleSpinner
                      className='spinner-app mx-auto'
                      style={{ margin: 'auto' }}
                      size={40}
                      color='var(--color-font-primary)'
                    />
                  </div>
                ) : (
                  hasMore && (
                    <div
                      className='text-secondary font-small fw-semibold text-center p-2 mt-2'
                      style={{ border: '1px solid #3D3D3D', borderRadius: '5px' }}
                      onClick={fetchMoreReservations}
                    >
                      {t('showMore')}
                    </div>
                  )
                )}
              </>
            )}
          </>
        )}

        <CustomerNav history={history} />
      </ContentApp>
    </div>
  );
};

export default Account;
